import React, { useEffect, useMemo, useState } from "react";
import { CalendarBlank, Plus, Trash, Check, X, User, CircleNotch, Warning } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../contexts/AuthContext";

const API = `${window.location.origin}/api`;

const MONTHS = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

/**
 * Dniówki tab — day-based salary tracking.
 * - Employee (mode=dniowki): sees own days, adds/removes pending days.
 * - Admin: sees all days, filters by employee, approves/rejects.
 */
export default function DniowkiTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");  // admin filter
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState(null);

  const notify = (kind, msg) => { setFlash({ kind, msg }); setTimeout(() => setFlash(null), 4000); };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (isAdmin && employeeId) params.set("employee_id", employeeId);
      const { data } = await axios.get(`${API}/dniowki?${params}`);
      setEntries(data || []);
    } catch (e) {
      console.error(e);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/employees`);
        const dniowkaOnly = (data || []).filter((e) => e.salary_config?.mode === "dniowki");
        setEmployees(dniowkaOnly);
      } catch (e) { console.error(e); }
    })();
  }, [isAdmin]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, employeeId]);

  const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month]);
  const daysArr = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const dayEntry = (d) => entries.find((e) => e.day === d && (!isAdmin || !employeeId || e.employee_id === employeeId));

  const toggleDay = async (day) => {
    if (isAdmin && !employeeId) { notify("err", "Wybierz pracownika."); return; }
    const existing = dayEntry(day);
    if (existing) {
      if (existing.status !== "pending" && !isAdmin) { notify("err", "Zatwierdzonej dniówki nie można usunąć."); return; }
      setBusyId(existing.id);
      try {
        await axios.delete(`${API}/dniowki/${existing.id}`);
        await load();
      } catch (e) {
        notify("err", e?.response?.data?.detail || "Błąd");
      } finally { setBusyId(null); }
      return;
    }
    setBusyId(`new-${day}`);
    try {
      const body = { year, month, day };
      if (isAdmin) body.employee_id = employeeId;
      await axios.post(`${API}/dniowki`, body);
      await load();
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd dodawania");
    } finally { setBusyId(null); }
  };

  const decide = async (id, status) => {
    let reason = null;
    if (status === "rejected") {
      reason = window.prompt("Powód odrzucenia (opcjonalnie):") || "";
    }
    setBusyId(id);
    try {
      await axios.post(`${API}/dniowki/${id}/decision`, { status, rejection_reason: reason });
      await load();
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd");
    } finally { setBusyId(null); }
  };

  const summary = useMemo(() => {
    const scoped = (isAdmin && employeeId) ? entries.filter((e) => e.employee_id === employeeId) : entries;
    const approved = scoped.filter((e) => e.status === "approved").length;
    const pending = scoped.filter((e) => e.status === "pending").length;
    return { approved, pending, total: approved + pending };
  }, [entries, employeeId, isAdmin]);

  const canInteract = !isAdmin || !!employeeId;

  return (
    <div className="space-y-6" data-testid="dniowki-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#0B1E3F] text-white flex items-center justify-center">
            <CalendarBlank className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold leading-tight">Dniówki</h2>
            <p className="text-xs text-muted-foreground">
              {isAdmin ? "Dni pracy sotrudników w systemie dniówkowym — akceptacja / edycja" : "Zaznacz dni, w których pracowałeś — admin je zatwierdza"}
            </p>
          </div>
        </div>
      </div>

      {flash && (
        <div className={`text-sm px-4 py-2 rounded border ${flash.kind === "ok" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
          {flash.msg}
        </div>
      )}

      <div className="bg-white rounded border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select className="input-field !w-auto" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="dniowki-month">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="input-field !w-auto" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="dniowki-year">
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {isAdmin && (
            <select className="input-field !w-auto min-w-[180px]" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="dniowki-employee">
              <option value="">— Wszyscy z systemem dniówka —</option>
              {employees.map((e) => (<option key={e._id} value={e._id}>{e.name}</option>))}
            </select>
          )}
          <div className="ml-auto text-xs text-slate-500 flex items-center gap-3">
            <span>Zatwierdzone: <b className="text-green-700">{summary.approved}</b></span>
            <span>Oczekujące: <b className="text-amber-700">{summary.pending}</b></span>
          </div>
        </div>

        {isAdmin && !employeeId && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            Aby zaznaczać / edytować dni — wybierz pracownika powyżej. Lista poniżej pokazuje wszystkie zgłoszenia z tego miesiąca.
          </div>
        )}

        {loading ? (
          <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
            <CircleNotch className="w-4 h-4 animate-spin" /> Ładowanie…
          </div>
        ) : (
          <>
            {/* Day grid — only shown when a specific employee is selected (or employee viewing self) */}
            {canInteract && (
              <div className="grid grid-cols-7 gap-1.5 mb-4">
                {daysArr.map((d) => {
                  const e = dayEntry(d);
                  const status = e?.status;
                  const isBusy = busyId === (e?.id || `new-${d}`);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      disabled={isBusy}
                      title={e?.note || (status ? `Status: ${status}` : "Kliknij aby zaznaczyć")}
                      className={`aspect-square rounded text-sm font-semibold flex items-center justify-center transition-colors border ${
                        !e ? "bg-white border-slate-200 hover:bg-slate-50 text-slate-600" :
                        status === "approved" ? "bg-green-100 border-green-300 text-green-800" :
                        status === "rejected" ? "bg-red-100 border-red-300 text-red-800 line-through" :
                        "bg-amber-100 border-amber-300 text-amber-800"
                      } ${isBusy ? "opacity-50" : ""}`}
                      data-testid={`dniowki-day-${d}`}
                    >
                      {isBusy ? <CircleNotch className="w-3 h-3 animate-spin" /> : d}
                    </button>
                  );
                })}
              </div>
            )}

            {/* List view — admin approvals live here */}
            {entries.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">Brak dniówek w tym miesiącu.</div>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm" data-testid="dniowki-list">
                {entries.map((e) => (
                  <li key={e.id} className="py-2 flex items-center gap-3" data-testid={`dniowki-item-${e.id}`}>
                    <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5 w-14 text-center">
                      {String(e.day).padStart(2, "0")}.{String(e.month).padStart(2, "0")}
                    </span>
                    {isAdmin && (
                      <span className="text-slate-700 flex items-center gap-1 min-w-[100px]">
                        <User className="w-3 h-3 text-slate-400" /> {e.employee_name}
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      e.status === "approved" ? "bg-green-100 text-green-800" :
                      e.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {e.status === "approved" ? "zatwierdzona" : e.status === "rejected" ? "odrzucona" : "oczekuje"}
                    </span>
                    {e.note && <span className="text-xs text-slate-500 truncate">{e.note}</span>}
                    {e.rejection_reason && (
                      <span className="text-xs text-red-600 flex items-center gap-1" title={e.rejection_reason}>
                        <Warning className="w-3 h-3" /> {e.rejection_reason}
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                      {isAdmin && e.status === "pending" && (
                        <>
                          <button
                            onClick={() => decide(e.id, "approved")}
                            className="p-1 hover:bg-green-100 rounded"
                            title="Zatwierdź"
                            data-testid={`dniowki-approve-${e.id}`}
                            disabled={busyId === e.id}
                          >
                            <Check className="w-4 h-4 text-green-700" />
                          </button>
                          <button
                            onClick={() => decide(e.id, "rejected")}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Odrzuć"
                            data-testid={`dniowki-reject-${e.id}`}
                            disabled={busyId === e.id}
                          >
                            <X className="w-4 h-4 text-red-700" />
                          </button>
                        </>
                      )}
                      {(isAdmin || e.status === "pending") && (
                        <button
                          onClick={async () => { setBusyId(e.id); try { await axios.delete(`${API}/dniowki/${e.id}`); await load(); } catch (er) { notify("err", er?.response?.data?.detail || "Błąd"); } finally { setBusyId(null); } }}
                          className="p-1 hover:bg-slate-100 rounded"
                          title="Usuń"
                          data-testid={`dniowki-delete-${e.id}`}
                          disabled={busyId === e.id}
                        >
                          <Trash className="w-4 h-4 text-slate-400" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
